import { atom, selector } from "recoil";
import _ from "lodash";
import { STATIC_STRATEGY_DEFAULT } from "@pages/backtest/define";
import { IPaginationRequest, IPagination, ITimeseriesDataFrame } from 'types/api';
import {
  IBTSObject,
  IResBacktest,
  IResBacktestHistory,
  IBacktestStaticStrategy,
  IBacktestStaticConfig,
} from 'types/backtest';
import axios from "@utils/axios";

export const staticBacktestMemoState = atom<string[]>({
  key: "staticBacktestMemoState",
  default: [],
});

export const staticBacktestPortPriceIndexState = atom<ITimeseriesDataFrame | null>({
  key: "staticBacktestPortPriceIndexState",
  default: null,
});

export const staticBacktestOverviewState = atom<IBTSObject[]>({
  key: "staticBacktestOverviewState",
  default: [],
});

export const staticBacktestYearlyReturnState = atom<IBTSObject[]>({
  key: "staticBacktestYearlyReturnState",
  default: [],
});

export const staticBacktestPortDrawDownState = atom<IBTSObject[]>({
  key: "staticBacktestPortDrawDownState",
  default: [],
});

export const staticBacktestTotalCorrelationDailyState = atom<IBTSObject[]>({
  key: "staticBacktestTotalCorrelationDailyState",
  default: [],
});

export const staticBacktestTotalCorrelationWeeklyState = atom<IBTSObject[]>({
  key: "staticBacktestTotalCorrelationWeeklyState",
  default: [],
});

export const staticBacktestTotalCorrelationMonthlyState = atom<IBTSObject[]>({
  key: "staticBacktestTotalCorrelationMonthlyState",
  default: [],
});

export const staticBacktestAssetCorrelationDailyState = atom<IBTSObject[][]>({
  key: "staticBacktestAssetCorrelationDailyState",
  default: [],
});

export const staticBacktestAssetCorrelationWeeklyState = atom<IBTSObject[][]>({
  key: "staticBacktestAssetCorrelationWeeklyState",
  default: [],
});

export const staticBacktestAssetCorrelationMonthlyState = atom<IBTSObject[][]>({
  key: "staticBacktestAssetCorrelationMonthlyState",
  default: [],
});

//strategies
export const staticBacktestStrategiesState = atom<IBacktestStaticStrategy[]>({
  key: "staticBacktestStrategiesState",
  default: [_.cloneDeep(STATIC_STRATEGY_DEFAULT)],
});

export const staticBacktestBacktestDetailState = atom<IResBacktest | null>({
  key: "staticBacktestBacktestDetailState",
  default: null,
});

export const staticBacktestLoadingState = atom<boolean>({
  key: "staticBacktestLoadingState",
  default: false,
});

export const staticBacktestHistoriesQueryPage = atom<number>({
  key: "staticBacktestHistoriesQueryPage",
  default: 0,
});

export const staticBacktestHistoriesQueryPageSize = atom<number>({
  key: "staticBacktestHistoriesQueryPageSize",
  default: 10,
});

/**
 * 이력 목록 정렬. **서버로 보낸다.**
 *
 * 목록이 서버 페이지네이션이라 정렬도 서버가 해야 한다. 클라이언트에서 정렬하면
 * 지금 화면에 있는 한 페이지만 줄을 세운다 — 이력이 쌓일수록 "가장 최근 것" 을
 * 찾으려고 눌러도 엉뚱한 답이 나온다.
 *
 * `field` 는 **DB 컬럼명**이다. 서버가 `BacktestHistoryModel.__table__.columns` 로
 * 검증하고 모르는 값이면 조용히 `created_at` 으로 되돌린다.
 */
export type BacktestHistoriesSort = { field: string; order: "asc" | "desc" };

/** 서버 기본값과 같다. 화면도 이 상태로 시작해야 "정렬 안 된 것처럼" 보이지 않는다. */
export const BACKTEST_HISTORIES_DEFAULT_SORT: BacktestHistoriesSort = {
  field: "created_at",
  order: "desc",
};

export const staticBacktestHistoriesSort = atom<BacktestHistoriesSort>({
  key: "staticBacktestHistoriesSort",
  default: BACKTEST_HISTORIES_DEFAULT_SORT,
});

export const staticBacktestHistoriesQuery = selector<IPagination<IResBacktestHistory>>({
  key: "staticBacktestHistories",
  get: async ({ get }) => {
    let url = "backtest/histories?";
    const page = get(staticBacktestHistoriesQueryPage) + 1;
    const size = get(staticBacktestHistoriesQueryPageSize);
    const sort = get(staticBacktestHistoriesSort);

    if (!!page) url += `page=${page}&`;
    if (!!size) url += `size=${size}&`;
    url += `orderBy=${sort.field}&order=${sort.order}`;

    const response = await axios.get(url);
    return response.data;
  },
});

export const staticBacktestInitialConfig: IBacktestStaticConfig = {
  cost: 0,
  name: "",
  bt_end: "",
  bt_start: "",
  initial_amount: 100,
  after_tax: false,
  benchmark: [""],
  data_freq: "D",
  port_list: [],
  strategies: [],
  currency: "KRW",
};

export const staticBacktestConfigState = atom<IBacktestStaticConfig>({
  key: "staticBacktestConfigState",
  default: staticBacktestInitialConfig,
});
